import React, { useEffect, useMemo, useState } from "react";
import {
	Card,
	CardActionArea,
	CardContent,
	CardMedia,
	Typography,
	Button,
	Box,
	Checkbox,
	SpeedDial,
	SpeedDialAction,
	Tooltip,
	CircularProgress,
} from "@mui/material";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import "./ProductCard.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { ASSETS_BASE_URL, RSPL_LIST } from "../../config";
import { useTranslation } from "react-i18next";
import { ROUTES, UI_TEXT } from "../../constants";
import formatCurrency from "../../utilities/currency";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { saveRequestList, saveProductForLater } from "../../services/productCardService";
import { toast } from "../../context/ToastContext";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { useOptions } from "../../context/optionContext";
import { buildRsplCodeToLabelMap, getRsplLabelForCode, parseRsplList } from "../../utilities/rspl";

const ProductCard = ({
	product,
	machineID,
	onAddToCart,
	onViewDetails,
	onSaveForLater,
	onDelete,
	showDial,
	showSavedForLaterDial,
	showCheckbox = false,
	checked = false,
	onCheckedChange,
}) => {
	const { optionKeys } = useOptions();
	const resolvedRsplList = useMemo(
		() => parseRsplList(optionKeys?.rsplList, RSPL_LIST),
		[optionKeys?.rsplList]
	);
	const rsplMapping = useMemo(
		() => buildRsplCodeToLabelMap(resolvedRsplList),
		[resolvedRsplList]
	);
	const { addToCart } = useCart();
	const { selectedSite, selectedSiteCurrency, currencySymbol } = useSite();
	const { user } = useAuth();
	const { stockImageUrl } = useBrandingAssets();
	const [imageSrc, setImageSrc] = useState(stockImageUrl || `${ASSETS_BASE_URL}/stock.png`);
	const [isLoading, setIsLoading] = useState(false); // To manage the loading state
	const [isSuccess, setIsSuccess] = useState(false); // To track success state
	const navigate = useNavigate();
	const {t} = useTranslation();
	const [price, setPrice] = useState(0);
	const triggerToast = (message, severity = "success") => {
		if (!message) return;
		toast.trigger({ message, severity });
	};
	// useEffect(() => {
	// 	const checkImageExists = async () => {
	// 		const productImageUrl = `${ASSETS_BASE_URL}/ProductImages/TMB/${product.product_number}.JPG`;
	// 		try {
	// 			const response = await fetch(productImageUrl, { method: "HEAD" });
	// 			if (response.ok) {
	// 				setImageSrc(productImageUrl);
	// 			}
	// 		} catch (error) {
	// 			console.warn("Product image not found, using default image.");
	// 		}
	// 	};

	// 	checkImageExists();
	// 	// eslint-disable-next-line
	// }, [product.product_number]);

	useEffect(() => {
		if (product && selectedSiteCurrency) {
			const key = `price_${selectedSiteCurrency}`;
			const currencyPrice = product[key];
			const finalPrice = product.purchasable ? currencyPrice : 0;

			setPrice(finalPrice);
		}
	}, [product, selectedSiteCurrency]);

	const handleViewProduct = (productid) => {
		navigate(`/machines/${machineID}/${productid}`);
		
	};

	const handleDeleteClick = (event) => {
		event.stopPropagation();
		onDelete(product.product_id, machineID);
	};

	const handleAddToRequest = async (event) => {
		event.stopPropagation();

		// Start the loading state and reset success state
		setIsLoading(true);
		setIsSuccess(false);
		try {
			// Call the addToCart function (which you've defined to add the product)
			// Set success state
			const req_body = {
				machine_id: machineID,
				product_id: product.product_id,
				site_number: selectedSite,
				user_id: user.user_id,
			};

			try {
				const response = await saveRequestList(req_body);
				if (response.status) {
					setIsSuccess(true);
					triggerToast(t('ALERT_MESSAGES.PRODUCT_ADDED_TO_REQUEST', { productName: product.product_name }), "success");
					setTimeout(() => setIsSuccess(false), 1200);
				} else {
					triggerToast(t('ALERT_MESSAGES.PRODUCT_ALREADY_IN_REQUEST', { productName: product.product_name }), "warning");
				}
			} catch (error) {
				console.error("Error fetching product name:", error);
			}

			// Reset the button text back to "Add to Cart" after 2 seconds
			// 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccess(false);
			triggerToast(
				t('ALERT_MESSAGES.ERROR_ADDING_TO_REQUEST', { defaultValue: 'Unable to add product to Request List' }),
				"error"
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleSavedForLater = async (event) => {
		event.stopPropagation();

		try {
			// Call the addToCart function (which you've defined to add the product)
			// Set success state
			const req_body = {
				machine_id: machineID,
				product_id: product.product_id,
				site_number: selectedSite,
				user_id: user.user_id,
			};

			try {
				const response = await saveProductForLater(req_body);
				if (response.status) {
					triggerToast(`Product ${product.product_number} added to Saved for Later`, "success");
				} else {
					triggerToast(t('ALERT_MESSAGES.PRODUCT_ALREADY_IN_SFL', { productNumber: product.product_number }), "warning");
                }
			} catch (error) {
				console.error("Error fetching product name:", error);
			}

			// Reset the button text back to "Add to Cart" after 2 seconds
			// 2 seconds delay
		} catch (error) {
			triggerToast(
				t('ALERT_MESSAGES.ERROR_SAVING_FOR_LATER', { defaultValue: 'Unable to save product for later' }),
				"error"
			);
		}
	};



	const handleAddToCart = async (event) => {
		event.stopPropagation();

		// Start the loading state and reset success state
		setIsLoading(true);
		setIsSuccess(false);
		try {
			// Call the addToCart function (which you've defined to add the product)
			await addToCart({ ProductID: product.product_id, Quantity: 1, Site: selectedSite });

			// Set success state
			setIsSuccess(true);
			triggerToast(
				t('ALERT_MESSAGES.PRODUCT_ADDED_TO_CART', { productNumber: product.product_number }),
				"success"
			);
			setTimeout(() => setIsSuccess(false), 2000); // 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccess(false);
			triggerToast(
				t('ALERT_MESSAGES.ERROR_ADDING_TO_CART', { defaultValue: 'Unable to add product to cart' }),
				"error"
			);
		} finally {
			// Stop the loading spinner after a delay and reset the button state
			setIsLoading(false);
		}
	};

    const productImage = (() => {
        const raw = product?.asset_data?.images?.[0];
        if (raw) {
            if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
            const path = raw.startsWith("/") ? raw : `/${raw}`;
            return `${ASSETS_BASE_URL}${path}`;
        }
        return `${ASSETS_BASE_URL}/ProductImages/TMB/${product.product_number}.JPG`;
    })();

    return (
		<>
			<Card className="product-card" sx={{ position: "relative" }}>
				{showCheckbox && (
					<Box
						sx={{
							position: "absolute",
							top: 6,
							left: 6,
							zIndex: 3,
							background: "rgba(255,255,255,0.9)",
							borderRadius: "8px",
						}}
					>
						<Checkbox
							checked={!!checked}
							onClick={(e) => e.stopPropagation()}
							onChange={(e) => {
								e.stopPropagation();
								onCheckedChange?.(product, e.target.checked);
							}}
							size="small"
						/>
					</Box>
				)}
				<CardActionArea className="product-card-content" onClick={() => handleViewProduct(product.product_id)}>
					<CardMedia
						component="img"
                    image={productImage}
                    alt={product.product_name}
                    sx={{ objectFit: "cover" }}
                    className="product-img"
                    onError={(e) => {
                        e.target.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
                    }}
                />

					<CardContent sx={{ paddingBottom: "0px", textAlign: "center" }}>
						<Tooltip title={product.product_name}>
							<Typography
								variant="body1"
								sx={{
									fontWeight: "bold",
									lineHeight: "1.2rem",
									marginBottom: "5px",
									display: "-webkit-box",
									overflow: "hidden",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									textOverflow: "ellipsis",
									minHeight: "2.4rem",
								}}>
								{product.product_name}
							</Typography>
						</Tooltip>
						<Tooltip title={product.product_name}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									fontSize: "11px",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									maxWidth: "150px",
								}}>
								<b>{t('UI_TEXT.PART')}</b> {product.product_number}
							</Typography>
						</Tooltip>
						<Tooltip title={product.product_rspl_code}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									fontSize: "11px",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									maxWidth: "150px",
								}}>
								<b>{t('UI_TEXT.PART_TYPE')}</b>{" "}
								{getRsplLabelForCode(product.product_rspl_code, rsplMapping, t('UI_TEXT.OTHER'))}
							</Typography>
						</Tooltip>
						<Box mt={1} display="flex" alignItems="center" justifyContent="center" minHeight="25px">
							<Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "14px" }}>
								 {price === "0.00" || price === 0
									? ""
									: formatCurrency(
										typeof price === "string" ? parseFloat(price) : price,
										selectedSiteCurrency
								)}
								{/* {price === "0.00" || price === 0
									? ""
									: `${currencySymbol}${
											typeof price === "string" ? parseFloat(price).toFixed(2) : price.toFixed(2)
									  }`} */}
							</Typography>
						</Box>
					</CardContent>

					{/* SpeedDial with Save and Delete Actions */}
					{showDial ? (
						<>
							<SpeedDial
								ariaLabel="Product actions"
								icon={<MoreHorizIcon variant="Outlined" sx={{ color: "primary.main", fontSize: 24 }} />}
								direction="down"
								className="speed-dial-custom"
								sx={{
									// Inline css needed here
									width: "10px",
									height: "10px",
									marginTop: -1,
									marginRight: 0,
									boxShadow: "none",
									backgroundColor: "transparent",
									"& .MuiFab-primary": {
										backgroundColor: "transparent",
										boxShadow: "none",
										"&:hover": {
											backgroundColor: "transparent",
										},
									},
								}}>
								<SpeedDialAction
									icon={<DeleteIcon />}
									tooltipTitle={t("UI_TEXT.TOOLTIP_DELETE")}
									onClick={handleDeleteClick}
								/>
								{Number(product.purchasable) === 1 && (
									<SpeedDialAction
										icon={<BookmarkAddIcon />}
										tooltipTitle="Save for Later"
										onClick={handleSavedForLater}
									/>
								)}
							</SpeedDial>
						</>
					) : (
						<></>
					)}

					{showSavedForLaterDial && Number(product.purchasable) === 1 ? (
						<>
							<SpeedDial
								ariaLabel="Product actions"
								icon={<MoreHorizIcon variant="Outlined" sx={{ color: "primary.main", fontSize: 24 }} />}
								direction="down"
								className="speed-dial-custom"
								sx={{
									// Inline css needed here
									width: "10px",
									height: "10px",
									marginTop: -1,
									marginRight: 0,
									boxShadow: "none",
									backgroundColor: "transparent",
									"& .MuiFab-primary": {
										backgroundColor: "transparent",
										boxShadow: "none",
										"&:hover": {
											backgroundColor: "transparent",
										},
									},
								}}>
								<SpeedDialAction
									icon={<BookmarkAddIcon />}
									tooltipTitle="Save for Later"
									onClick={handleSavedForLater}
								/>
							</SpeedDial>
						</>
					) : (
						<></>
					)}

					<div className="atc-button">
						{Number(product.purchasable) === 1 ? (
							<Button
								size="small"
								variant="contained"
								sx={{
									backgroundColor: "var(--primary-main)",
									padding: "8px 15px",
									fontWeight: "bolder",
								}}
								data-tour="product-add-to-cart"
								onClick={handleAddToCart}
								disabled={isLoading} // Disable the button while loading or after success
							>
								{isLoading ? (
									<>
										<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
										{t('BUTTON.ADDING')}
									</>
								) : isSuccess ? (
									<>
										<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
										{t('BUTTON.ADDED')}
									</>
								) : (
									t('LABELS.ADD_TO_CART')
								)}
							</Button>
						) : (
							<Button
								size="small"
								variant="contained"
								sx={{
									backgroundColor: "var(--primary-main)",
									padding: "8px 15px",
									fontWeight: "bolder",
								}}
								data-tour="product-add-to-cart"
								onClick={handleAddToRequest}
								disabled={isLoading} // Disable the button while loading or after success
							>
								{isLoading ? (
									<>
										<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
										{t('BUTTON.ADDING')}
									</>
								) : isSuccess ? (
									<>
										<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
										{t('BUTTON.ADDED')}
									</>
								) : (
									t('BUTTON.ADD_TO_REQUEST_LIST')
								)}
							</Button>
						)}
					</div>
				</CardActionArea>
			</Card>
		</>
	);
};

export default ProductCard;
